import {
  ASSAY_RAMP, PROSPECT_BLACK, PROSPECT_WHITE, SOIL_STOPS, gradeSortKey, rampColour,
} from '../prospect/prospectStyle';

/**
 * OCR gold values — gold assays read off the tables in WAMEX A-number reports
 * by the table reader (goldfinder-llm-kb, au_pathfinder.table_read_*), placed
 * from the coordinates printed beside them.
 *
 * LOCAL ONLY. The layer lives in the local KB Postgres (127.0.0.1:5433) and is
 * published by the local GeoServer container as `ne:ocr_gold_values`; it is not
 * in production. The overlay is therefore offered only when a WMS endpoint is
 * configured, which a production build never has.
 *
 * Served as vector tiles straight from WMS GetMap rather than through GWC. The
 * table is still being loaded by the extraction pipeline, and a GWC tile cache
 * would keep serving whatever it rendered first; GetMap renders from the table
 * on every request, so what the pipeline wrote is what the map shows.
 */
export const OCR_GOLD_WMS = process.env.REACT_APP_OCR_GOLD_WMS
  || (process.env.NODE_ENV === 'development' ? 'http://localhost:8600/geoserver/ne/wms' : null);

export const OCR_GOLD_AVAILABLE = !!OCR_GOLD_WMS;

/**
 * Through the API's admin-only layers route (gold-finder-api routes/layers.js)
 * rather than a GeoServer directly: ne:ocr_gold_values is read-restricted, and
 * a deployed build reaches it only as an admin with a session cookie.
 */
/** Served by the local GeoServer (a dev server), not a deployed one. */
export const OCR_GOLD_LOCAL = !!OCR_GOLD_WMS && /\/\/(localhost|127\.0\.0\.1)[:/]/.test(OCR_GOLD_WMS);

export const OCR_GOLD_RESTRICTED = !!OCR_GOLD_WMS && /\/service\/api\/layers\//.test(OCR_GOLD_WMS);

export const OCR_GOLD_LAYER = 'ne:ocr_gold_values';
/** MVT source-layer name: the GeoServer layer name without the workspace. */
export const OCR_GOLD_SOURCE_LAYER = 'ocr_gold_values';
export const OCR_GOLD_VECTOR_SRC = 'ocr-gold';

/**
 * Nothing is drawn below this. Tiles are not thinned: a z7 tile over the
 * Goldfields is ~3 MB and takes GeoServer ~1.7 s to encode, a z10 one under a
 * second.
 */
export const OCR_GOLD_VECTOR_MINZOOM = 8;
/** Deepest tile requested; MapLibre over-zooms past it. */
const VECTOR_MAXZOOM = 14;

export function buildOcrGoldSource() {
  // `styles=point` - the plain built-in style. The layer's own SLD is a set of
  // filtered rules, and WMS vector-tile output applies style rules, so a
  // feature no rule matches would silently never reach the tile.
  return {
    type: 'vector',
    tiles: [`${OCR_GOLD_WMS}?service=WMS&version=1.1.1&request=GetMap`
      + `&layers=${encodeURIComponent(OCR_GOLD_LAYER)}&styles=point`
      + `&format=${encodeURIComponent('application/vnd.mapbox-vector-tile')}`
      + '&srs=EPSG:3857&bbox={bbox-epsg-3857}&width=512&height=512'],
    tileSize: 512,
    minzoom: OCR_GOLD_VECTOR_MINZOOM,
    maxzoom: VECTOR_MAXZOOM,
  };
}

/**
 * The table reader's own confidence in each read. One sidebar checkbox each,
 * held in overlayVisibility as booleans so presets carry them. A key that is
 * absent - a preset saved before these existed - means shown.
 */
export const OCR_GOLD_CONFIDENCE = [
  { value: 'low', label: 'Low', key: 'ocrGoldLow' },
  { value: 'medium', label: 'Medium', key: 'ocrGoldMedium' },
  { value: 'high', label: 'High', key: 'ocrGoldHigh' },
];

export const ocrGoldConfidenceShown = (overlayVisibility) =>
  OCR_GOLD_CONFIDENCE.filter((c) => overlayVisibility?.[c.key] !== false).map((c) => c.value);

export const OCR_GOLD_LAYER_ID = 'ocr-gold';

/**
 * Drawn exactly as the prospect overlay draws a recovered soil sample (see
 * prospectStyle `soils`): the soil ramp at the soil overlay's 0/20/50/100 ppb
 * stops, a BLACK edge because the value was read off a page rather than keyed
 * into an assay table, and WHITE where no ppb could be derived. The same
 * measurement recovered the same way reads the same on the map.
 *
 * `au_ppb` is Postgres numeric, which arrives in a tile as a string -
 * rampColour and gradeSortKey both convert before comparing.
 */
export function ocrGoldLayer(confidences) {
  return {
    id: OCR_GOLD_LAYER_ID,
    type: 'circle',
    source: OCR_GOLD_VECTOR_SRC,
    'source-layer': OCR_GOLD_SOURCE_LAYER,
    filter: ['in', ['get', 'confidence'], ['literal', confidences]],
    layout: { 'circle-sort-key': gradeSortKey('au_ppb') },
    paint: {
      'circle-color': ['case',
        ['==', ['to-string', ['coalesce', ['get', 'au_ppb'], '']], ''], PROSPECT_WHITE,
        rampColour('au_ppb', SOIL_STOPS)],
      'circle-stroke-color': PROSPECT_BLACK,
      'circle-stroke-width': 1.4,
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 2.5, 11, 3, 14, 5, 17, 7.5],
    },
  };
}

/** Legend rows, from the same stops and colours the layer uses. */
export const OCR_GOLD_LEGEND = [
  ...SOIL_STOPS.map((from, i) => ({
    colour: ASSAY_RAMP[i],
    label: i < SOIL_STOPS.length - 1 ? `> ${from}` : `> ${from} ppb`,
  })),
  { colour: PROSPECT_WHITE, label: 'no ppb' },
];

/** For click testing, and for useTopLayers. */
export const OCR_GOLD_INTERACTIVE = [OCR_GOLD_LAYER_ID];
export const OCR_GOLD_ORDER = [OCR_GOLD_LAYER_ID];
